import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface ProductTourContextType {
  role: string | null;
  tenantId: string | null;
  tourEnabled: boolean;
  isImpersonating: boolean;
  isMobile: boolean;
}

const ProductTourContext = createContext<ProductTourContextType>({
  role: null,
  tenantId: null,
  tourEnabled: false,
  isImpersonating: false,
  isMobile: false,
});

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

export function ProductTourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const value = useMemo(() => {
    const role = user?.role ?? null;
    const tenantId = user?.tenantId ?? null;
    // Disable tours during admin impersonation and on mobile
    const isImpersonating = !!(user as unknown as Record<string, unknown>)?.isImpersonating;
    const tourEnabled = !!user && !isImpersonating && !isMobile;

    return { role, tenantId, tourEnabled, isImpersonating, isMobile };
  }, [user, isMobile]);

  return (
    <ProductTourContext.Provider value={value}>
      {children}
    </ProductTourContext.Provider>
  );
}

export function useProductTourContext() {
  return useContext(ProductTourContext);
}
